import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError, take} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {ProjectAttributes} from "../../models/project/project-attribute";
import {ToastService} from "../toast/toast.service";
import {TranslateService} from "@ngx-translate/core";

/**
 * Service for populating a couple selection items in the project details edit screen, using data retrieved from the MDM API.
 */
@Injectable({providedIn: 'root'})
export class ProjectAttributeService {
    private readonly httpClient = inject(HttpClient);
    private readonly toastService = inject(ToastService);
    private readonly translateService = inject(TranslateService);

    /**
     * Gets an object which combines the different project selection items.
     *
     * @returns The project attribute object.
     */
    public getAttributes(): Observable<ProjectAttributes> {
        return this.httpClient.get<ProjectAttributes>(`${environment.apiUrl}/projects/attributes`).pipe(
            take(1),
            catchError((error) => this.handleError(error, 'ERRORS.project_attributes_not_retrieved'))
        );
    }

    /**
     * Handles an http error and returns an observable that throws the error.
     *
     * <p>As a side effect, an error toast is shown to the user.
     *
     * @param error Http error response.
     * @param translationKey Translation key for the error message.
     * @returns An observable that throws the error.
     */
    private handleError(error: HttpErrorResponse, translationKey: string): Observable<never> {
        console.error(error);
        this.toastService.openError(error.status ?? 0, this.translateService.instant(translationKey));
        return throwError(() => error);
    }
}
